import type { FC, ReactNode } from 'react'
import type { HTMLMotionProps } from 'framer-motion'

import { motion, useReducedMotion } from 'framer-motion'

const EASE: [number, number, number, number] = [0.22, 1, 0.36, 1]

interface FadeInProps extends Omit<HTMLMotionProps<'div'>, 'children'> {
    children: ReactNode
    delay?: number
    y?: number
    duration?: number
    once?: boolean
}

// Single-idea reveal: fade + small vertical lift. Collapses to a pure fade with reduced motion.
export const FadeIn: FC<FadeInProps> = ({
    children,
    delay = 0,
    y = 16,
    duration = 0.5,
    once = true,
    ...rest
}): ReactNode => {
    const reduce = useReducedMotion()

    return (
        <motion.div
            initial={{ opacity: 0, y: reduce ? 0 : y }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once, margin: '-80px' }}
            transition={{ duration: reduce ? 0.2 : duration, delay, ease: EASE }}
            {...rest}
        >
            {children}
        </motion.div>
    )
}

interface StaggerProps extends Omit<HTMLMotionProps<'div'>, 'children'> {
    children: ReactNode
    staggerChildren?: number
    delayChildren?: number
}

export const Stagger: FC<StaggerProps> = ({
    children,
    staggerChildren = 0.12,
    delayChildren = 0.05,
    ...rest
}): ReactNode => {
    const reduce = useReducedMotion()

    return (
        <motion.div
            initial='hidden'
            whileInView='visible'
            viewport={{ once: true, margin: '-80px' }}
            variants={{
                hidden: {},
                visible: {
                    transition: {
                        staggerChildren: reduce ? 0 : staggerChildren,
                        delayChildren: reduce ? 0 : delayChildren
                    }
                }
            }}
            {...rest}
        >
            {children}
        </motion.div>
    )
}

interface StaggerItemProps extends Omit<HTMLMotionProps<'div'>, 'children'> {
    children: ReactNode
    y?: number
}

export const StaggerItem: FC<StaggerItemProps> = ({ children, y = 18, ...rest }): ReactNode => {
    const reduce = useReducedMotion()

    return (
        <motion.div
            variants={{
                hidden: { opacity: 0, y: reduce ? 0 : y },
                visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: EASE } }
            }}
            {...rest}
        >
            {children}
        </motion.div>
    )
}
